/**
 * One adapter per kind of export.
 *
 * A driver only knows how to read its own backend, how to relaunch itself and,
 * where the file sits behind a link that expires, how to mint a fresh one;
 * everything else — polling cadence, retries, persistence, the dock and the
 * drawer — belongs to {@link useExportJobsStore}. Adding an export to the
 * product means adding an entry here and calling `register()` at launch: no new
 * interface to write.
 *
 * A burned video is followed by its burn — the uid it is registered under, since
 * `subtitle_burn_get` is addressed by the burn and not by the file it fills —
 * and handed over through `createDownload`'s signed link. A zip of several
 * assets has no backend route and the clip editor has not shipped: neither has a
 * driver, and the store follows no job it has no driver for.
 */

import { createDownload } from '@/api/assets/downloads'
import { createSubtitleBurn, getSubtitleBurn, type SubtitleBurnCodec } from '@/api/subtitles/burns'
import type { ExportJob, ExportJobDriver, ExportJobKind, ExportJobPatch } from './exportJobs.types'

/** Payload a zip job carries so it can be polled and relaunched after a reload. */
export interface ZipExportMeta {
  organizationId: string
  workspaceId: string | null
  assetIds: string[]
}

/** Payload a burned-video job carries: the track it burns and the file the render fills. */
export type BurnedVideoExportMeta = {
  subtitleId: string
  assetId: string
  assetFileId: string
  videoFormat: SubtitleBurnCodec
}

const GENERIC_ERROR = { key: 'assets.transfers.exports.errorGeneric' }

/** A job's payload read back as a burn's, or `null` when it is not one — it round-trips through storage. */
function burnMetaOf(meta: Record<string, unknown>): BurnedVideoExportMeta | null {
  const { subtitleId, assetId, assetFileId, videoFormat } = meta
  if (typeof subtitleId !== 'string' || typeof assetId !== 'string' || typeof assetFileId !== 'string') {
    return null
  }
  if (videoFormat !== 'h264' && videoFormat !== 'h265') return null
  return { subtitleId, assetId, assetFileId, videoFormat }
}

/** A signed link to the burned file. It expires, so it is minted when the file is handed over. */
async function mintLink(meta: BurnedVideoExportMeta): Promise<string> {
  const minted = await createDownload({ asset_id: meta.assetId, asset_file_id: meta.assetFileId })
  return minted.url
}

const burnedVideoDriver: ExportJobDriver = {
  async poll(job: ExportJob): Promise<ExportJobPatch> {
    const meta = burnMetaOf(job.meta)
    if (!meta) return { status: 'failed', error: GENERIC_ERROR }

    const burn = await getSubtitleBurn(meta.subtitleId, job.id)
    switch (burn.processing_status) {
      case 'completed':
        // Minted now so the drawer can offer the file and an auto-download can start at once.
        return {
          status: 'ready',
          progress: 100,
          filename: burn.output_filename ?? null,
          sizeBytes: burn.size_bytes ?? null,
          downloadUrl: await mintLink(meta),
        }
      case 'failed':
      case 'cancelled':
        return {
          status: 'failed',
          error: burn.processing_error ? { text: burn.processing_error } : GENERIC_ERROR,
        }
      case 'processing':
        return { status: 'running', progress: burn.processing_progress }
      default:
        return { status: 'pending', progress: burn.processing_progress }
    }
  },

  async restart(job: ExportJob) {
    const meta = burnMetaOf(job.meta)
    if (!meta) return null
    const burn = await createSubtitleBurn(meta.subtitleId, { video_codec: meta.videoFormat })
    const next: BurnedVideoExportMeta = { ...meta, assetId: burn.asset_id, assetFileId: burn.asset_file_id }
    return { id: burn.uid, meta: next }
  },

  async resolveDownloadUrl(job: ExportJob) {
    const meta = burnMetaOf(job.meta)
    return meta ? mintLink(meta) : null
  },
}

export const EXPORT_JOB_DRIVERS: Record<ExportJobKind, ExportJobDriver | undefined> = {
  zip: undefined,
  'burned-video': burnedVideoDriver,
  // The clip editor registers its own exports here when it ships.
  clip: undefined,
}
